import BaseComponent from '../base-component/base-component'
import {
  autoUpdate,
  computePosition,
  offset,
  flip,
  shift,
  arrow,
} from '@floating-ui/dom'
import { getTemplateFirstChildContent } from '../../utils/dom'
import { Middleware } from '@floating-ui/core'
import { Nullable } from '../../types/common'

class ClipboardButtonTooltip extends BaseComponent {
  element: HTMLElement
  arrow: Nullable<HTMLElement>

  constructor() {
    super()

    const template = this.querySelector(
      `[data-clipboard-button-tooltip-template="${this.id}"]`,
    )

    this.element = getTemplateFirstChildContent(template)
    this.arrow = this.element.querySelector('[data-float-element-arrow]')
  }

  connectedCallback(): void {
    this.init()
  }

  init = (): void => {
    if (!this.target) {
      return
    }

    autoUpdate(this.target, this, () => {
      computePosition(this.target, this, this.getOptions() as any).then(
        data => {
          this.updatePosition(data)
          this.updateArrow(data)
        },
      )
    })
  }

  updatePosition({ x, y }): void {
    Object.assign(this.style, {
      left: `${x}px`,
      top: `${y}px`,
    })
  }

  getOptions() {
    return {
      placement: this.placement,
      middleware: this.getMiddleware(),
      strategy: 'absolute',
    }
  }

  getMiddleware(): Middleware[] {
    const alignmentAxisOffset = this.dataset.alignmentAxisOffset || 0
    const mainAxisOffset = this.dataset.mainAxisOffset || 0

    const middleware = [
      offset({
        alignmentAxis: +alignmentAxisOffset,
        mainAxis: +mainAxisOffset,
      }),
      flip(),
      shift(),
    ]

    if (this.arrow) {
      middleware.push(arrow({ element: this.arrow }))
    }

    return middleware
  }

  updateArrow({ placement, middlewareData }): void {
    if (!this.arrow) {
      return
    }

    const { x: arrowX, y: arrowY } = middlewareData.arrow

    const staticSide = {
      top: 'bottom',
      right: 'left',
      bottom: 'top',
      left: 'right',
    }[placement.split('-')[0]]

    Object.assign(this.arrow.style, {
      left: arrowX != null ? `${arrowX}px` : '',
      top: arrowY != null ? `${arrowY}px` : '',
      right: '',
      bottom: '',
      [staticSide]: '-5px',
    })
  }

  mount(): void {
    if (!this.contains(this.element)) {
      this.appendChild(this.element)
    }
  }

  unmount(): void {
    if (this.element) {
      this.element.remove()
    }
  }

  show = (): void => {
    this.mount()

    this.setVisible(true)

    this.emit('show', {})
  }

  hide = (): void => {
    this.setVisible(false)

    this.unmount()

    this.emit('hide', {})
  }

  setVisible(isVisible: boolean): void {
    this.setAttribute('aria-hidden', isVisible ? 'false' : 'true')
  }

  get target(): Nullable<HTMLElement> {
    return document.querySelector(
      `[data-clipboard-button-tooltip-target-id="${this.id}"]`,
    )
  }

  get placement(): string {
    return this.dataset.placement || 'bottom-end'
  }
}

export default ClipboardButtonTooltip
